import React, { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Drawer, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import useLocales from '../../hooks/useLocales';
import useAuth from '../../hooks/useAuth';
import { PATH_DASHBOARD } from '../../routes/paths';
import Logo from '../../components/Logo';
import LogoIsoMd from '../../components/LogoIsoMd';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import sidebarConfig from './SidebarConfig';

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 88;

const RootStyle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  }
}));

type DashboardSidebarProps = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
  isCollapsed: boolean;
  onToggleCollapse: VoidFunction;
};

export default function DashboardSidebar({
  isOpenSidebar,
  onCloseSidebar,
  isCollapsed,
  onToggleCollapse
}: DashboardSidebarProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();
  const { translate } = useLocales();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderHeader = (collapsed = false) =>
    collapsed ? (
      <Box
        sx={{
          pt: 1.5,
          pb: 0,
          pl: 3,
          pr: 0.5,
          minHeight: 52,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Box
          component={RouterLink}
          to={isAuthenticated ? PATH_DASHBOARD.overview.root : '/'}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <LogoIsoMd />
        </Box>
        {isDesktop && (
          <Tooltip title={translate('menu.expand_sidebar')} placement="right">
            <IconButton size="small" onClick={onToggleCollapse} sx={{ color: 'text.secondary' }}>
              <KeyboardDoubleArrowRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ) : (
      <Box
        sx={{
          pt: 1.5,
          pb: 0,
          pl: 2.5,
          pr: 1.5,
          minHeight: 52,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Box
          component={RouterLink}
          to={isAuthenticated ? PATH_DASHBOARD.overview.root : '/'}
          sx={{ display: 'inline-flex' }}
        >
          <Logo />
        </Box>
        {isDesktop && (
          <Tooltip title={translate('menu.collapse_sidebar')} placement="right">
            <IconButton size="small" onClick={onToggleCollapse} sx={{ color: 'text.secondary' }}>
              <KeyboardDoubleArrowLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );

  const renderContent = (collapsed = false) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {renderHeader(collapsed)}
      <Scrollbar
        sx={{
          flexGrow: 1,
          '& .simplebar-content': { display: 'flex', flexDirection: 'column' }
        }}
      >
        <NavSection navConfig={sidebarConfig} collapsed={collapsed} />
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ pb: 1 }} />
      </Scrollbar>
    </Box>
  );

  return (
    <RootStyle collapsed={isDesktop ? isCollapsed : false}>
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent(false)}
        </Drawer>
      )}
      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
              bgcolor: 'background.paper',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen
              })
            }
          }}
        >
          {renderContent(isCollapsed)}
        </Drawer>
      )}
    </RootStyle>
  );
}
